/**
 * PSV-1336 Phase 4 — arcade-style combat visualization.
 *
 * Drives every visual feedback layer that reacts to server-driven combat
 * events (ShipDamaged / ShipKilled / ShipRespawned) plus a few local
 * trigger effects (muzzle flash on own fire, projectile trail every
 * frame).
 *
 * One instance lives in `ArenaController.start()` and is destroyed in
 * `ArenaController.stop()`. The owning controller calls `update(deltaMs)`
 * every Phaser frame after sprite positions have settled, so HP bars and
 * particles read from the freshest ship positions.
 *
 * Pooling: 200 reusable `Phaser.GameObjects.Graphics` particles, hidden
 * + repositioned on release rather than destroyed. Overflow (>200
 * concurrent) silently drops new particles — acceptable for a 2-player
 * smoke demo. HP bars are NOT pooled (one per active ship, lifecycle
 * matches sprite lifecycle).
 */

import Phaser from 'phaser';

const SHIP_LENGTH = 80; // matches ArenaController smoke tuning (bumped 42→80)
// PSV-1336 HP bar v2: 70×8 game-style bar with border + drop-shadow,
// positioned BELOW the 2-line "Name\n[Class]" label (was striking
// through it at the old offset). Label is at sprite.y + SHIP_LENGTH/2
// + 4, 2 lines × ~16px ≈ 32px tall → bar at +48 leaves ~12px clearance.
// PSV-1729 — Starblast-style status ARCS around the hull replace the
// floating bar. HP arc sweeps the top 240°; the own-ship energy arc is
// a thinner inner ring. Drawn as lineBetween polylines (the shared
// Graphics mock has no arc()).
const ARC_HP_RADIUS = SHIP_LENGTH / 2 + 6;
const ARC_ENERGY_RADIUS = ARC_HP_RADIUS - 7;
const ARC_SPAN_RAD = (Math.PI * 4) / 3; // 240°
const ARC_START_RAD = -Math.PI / 2 - ARC_SPAN_RAD / 2; // centered on top
const ARC_SEGMENTS = 24;
const ARC_HP_WIDTH = 3.5;
const ARC_ENERGY_WIDTH = 2.5;
const COLOR_ENERGY_ARC = 0x35c4f0;

const COLOR_HP_HIGH = 0x44ff44;
const COLOR_HP_MID = 0xffdd00;
const COLOR_HP_LOW = 0xff3333;
const COLOR_HP_BG = 0x1a1a1a;
// PSV-1336 — pulse fill alpha 1.0 ↔ 0.5 over 400ms each half (800ms
// full cycle) when HP < 30%, drawing attention to the low state.
const HP_LOW_THRESHOLD = 0.3;
const HP_PULSE_DURATION_MS = 400;

const HIT_FLASH_MS = 120;
const HIT_SPARK_COUNT_MIN = 8;
const HIT_SPARK_COUNT_MAX = 12;
const HIT_SPARK_SPEED_MIN = 80;
const HIT_SPARK_SPEED_MAX = 180;
const HIT_SPARK_LIFETIME_MIN = 250;
const HIT_SPARK_LIFETIME_MAX = 400;
const HIT_SPARK_COLORS = [0xffee44, 0xff8822];

const DEATH_PARTICLE_COUNT_MIN = 30;
const DEATH_PARTICLE_COUNT_MAX = 40;
const DEATH_PARTICLE_SPEED_MIN = 200;
const DEATH_PARTICLE_SPEED_MAX = 400;
const DEATH_PARTICLE_LIFETIME_MIN = 600;
const DEATH_PARTICLE_LIFETIME_MAX = 900;
const DEATH_PARTICLE_COLORS = [0xff3333, 0xff8822, 0xffee44, 0xffffff];
const DEATH_RING_RADIUS_START = 10;
const DEATH_RING_RADIUS_END = 60;
const DEATH_RING_LIFETIME_MS = 400;

const MUZZLE_COUNT = 6;
const MUZZLE_SPEED_MIN = 200;
const MUZZLE_SPEED_MAX = 300;
const MUZZLE_LIFETIME = 150;
const MUZZLE_COLORS = [0xffff00, 0xffffff];
const MUZZLE_OFFSET_FORWARD = SHIP_LENGTH / 2;

const TRAIL_LIFETIME = 200;
const TRAIL_SIZE = 2;
const TRAIL_COLOR = 0xffee44;
const TRAIL_ALPHA_START = 0.6;

// PSV-1902 — 200 starved under combined load (own exhaust runs every
// frame + muzzle flashes + one trail dot per projectile per frame): the
// SHOOTER's own trail was the first casualty while observers, with less
// particle pressure, still saw it.
const PARTICLE_POOL_SIZE = 512;
// PSV-1911 — low-end phones (<=4 cores) get half the pool: exhaust +
// flashes degrade gracefully (oldest particles recycle sooner) while
// the frame budget survives. jsdom/tests report no hardwareConcurrency
// and keep the full pool.
function particlePoolSize(): number {
  const cores =
    typeof navigator !== 'undefined' && typeof navigator.hardwareConcurrency === 'number'
      ? navigator.hardwareConcurrency
      : 0;
  return cores > 0 && cores <= 4 ? PARTICLE_POOL_SIZE / 2 : PARTICLE_POOL_SIZE;
}

const YOU_DIED_DURATION_MS = 3000;

const KILL_FEED_MAX = 5;
const KILL_FEED_LIFETIME_MS = 5000;
const KILL_FEED_SLIDE_DURATION_MS = 200;
const KILL_FEED_FADE_DURATION_MS = 400;
const KILL_FEED_LINE_HEIGHT = 20;

interface Particle {
  graphic: Phaser.GameObjects.Graphics;
  active: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  age: number;
  lifetime: number;
  size: number;
  color: number;
  alphaStart: number;
}

/**
 * PSV-1336 HP bar v2: split into two Graphics — `outer` holds the static
 * drop-shadow + border + dark background, `fill` holds the colored
 * health portion (plus shine highlight). Splitting lets the pulse tween
 * target `fill.alpha` while leaving the border/bg solid.
 */
interface HpBarEntry {
  outer: Phaser.GameObjects.Graphics;
  fill: Phaser.GameObjects.Graphics;
  sprite: Phaser.GameObjects.Sprite;
  hp: number;
  hpMax: number;
  /** PSV-1729 — own-ship energy fraction (null = no energy arc). */
  energyFrac: number | null;
  visible: boolean;
  pulseTween: Phaser.Tweens.Tween | null;
}

/**
 * PSV-1336 Phase 4 — hit flash now drives the textured ship sprite's
 * own tint via `setTintFill(0xffffff)` (silhouette mode) for the flash
 * duration, then restores the pre-flash tint on expiry. Sprite-side
 * tint is far cleaner than the previous overlay-Graphics approach
 * because the flash exactly matches the ship's visible shape.
 */
interface HitFlashEntry {
  sprite: Phaser.GameObjects.Sprite;
  originalTint: number;
  remaining: number;
}

interface DeathRingEntry {
  graphic: Phaser.GameObjects.Graphics;
  x: number;
  y: number;
  age: number;
}

interface KillFeedEntry {
  text: Phaser.GameObjects.Text;
  age: number;
}

export class EffectsManager {
  private readonly scene: Phaser.Scene;
  private readonly pool: Particle[] = [];
  private readonly hpBars = new Map<string, HpBarEntry>();
  private readonly hitFlashes = new Map<string, HitFlashEntry>();
  private readonly deathRings: DeathRingEntry[] = [];
  private readonly killFeedEntries: KillFeedEntry[] = [];
  private readonly killFeedAnchorX: number;
  private readonly killFeedAnchorY = 20;
  private youDiedText: Phaser.GameObjects.Text | null = null;
  private youDiedCountdownText: Phaser.GameObjects.Text | null = null;
  private youDiedRemaining = 0;
  private destroyed = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.killFeedAnchorX = scene.cameras.main.width - 20;

    for (let i = 0; i < particlePoolSize(); i++) {
      const g = scene.add.graphics();
      g.setVisible(false);
      this.pool.push({
        graphic: g,
        active: false,
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        age: 0,
        lifetime: 0,
        size: 2,
        color: 0xffffff,
        alphaStart: 1,
      });
    }
  }

  // ---------- HP bars ----------

  attachShipHpBar(
    shipSprite: Phaser.GameObjects.Sprite,
    userId: string,
    hpMax: number,
  ): void {
    if (this.hpBars.has(userId)) return;
    const outer = this.scene.add.graphics();
    outer.setDepth(10);
    const fill = this.scene.add.graphics();
    fill.setDepth(11);
    const entry: HpBarEntry = {
      outer,
      fill,
      sprite: shipSprite,
      hp: hpMax,
      hpMax,
      energyFrac: null,
      visible: true,
      pulseTween: null,
    };
    this.hpBars.set(userId, entry);
    this.redrawHpBar(entry);
  }

  detachShipHpBar(userId: string): void {
    const entry = this.hpBars.get(userId);
    if (entry === undefined) return;
    if (entry.pulseTween !== null) {
      entry.pulseTween.remove();
      entry.pulseTween = null;
    }
    entry.outer.destroy();
    entry.fill.destroy();
    this.hpBars.delete(userId);
  }

  updateHpBar(userId: string, hp: number, hpMax: number): void {
    const entry = this.hpBars.get(userId);
    if (entry === undefined) return;
    entry.hp = hp;
    entry.hpMax = hpMax;
    entry.visible = hp > 0;
    this.redrawHpBar(entry);
    // Start / stop the low-HP pulse based on new ratio. Skipped when
    // dead (visible=false) — the bar is hidden during respawn anyway.
    const ratio = entry.hpMax > 0 ? entry.hp / entry.hpMax : 0;
    const shouldPulse = entry.visible && ratio < HP_LOW_THRESHOLD && ratio > 0;
    if (shouldPulse && entry.pulseTween === null) {
      entry.pulseTween = this.scene.tweens.add({
        targets: entry.fill,
        alpha: { from: 1, to: 0.5 },
        duration: HP_PULSE_DURATION_MS,
        yoyo: true,
        repeat: -1,
      });
    } else if (!shouldPulse && entry.pulseTween !== null) {
      entry.pulseTween.remove();
      entry.pulseTween = null;
      entry.fill.setAlpha(1);
    }
  }

  /**
   * PSV-1729 — Starblast-style status arcs (replaces the PSV-1336 bar):
   * outer Graphics carries the dim full-span track(s); fill Graphics
   * (pulse-tween target) carries the colored HP arc + optional energy
   * arc. Both centered on the hull — positioning in update() puts these
   * Graphics AT the sprite, offset 0.
   */
  private redrawHpBar(entry: HpBarEntry): void {
    const outer = entry.outer;
    const fill = entry.fill;
    outer.clear();
    fill.clear();
    if (!entry.visible) return;
    const ratio = Math.max(0, Math.min(1, entry.hp / entry.hpMax));
    const color =
      ratio > 0.6 ? COLOR_HP_HIGH : ratio > 0.3 ? COLOR_HP_MID : COLOR_HP_LOW;

    // Dim tracks for the full sweep.
    this.strokeArc(outer, ARC_HP_RADIUS, 1, COLOR_HP_BG, ARC_HP_WIDTH, 0.75);
    if (entry.energyFrac !== null) {
      this.strokeArc(outer, ARC_ENERGY_RADIUS, 1, COLOR_HP_BG, ARC_ENERGY_WIDTH, 0.6);
    }
    // Colored fills clipped to their fractions.
    if (ratio > 0) {
      this.strokeArc(fill, ARC_HP_RADIUS, ratio, color, ARC_HP_WIDTH, 1);
    }
    if (entry.energyFrac !== null && entry.energyFrac > 0) {
      this.strokeArc(
        fill,
        ARC_ENERGY_RADIUS,
        Math.min(1, entry.energyFrac),
        COLOR_ENERGY_ARC,
        ARC_ENERGY_WIDTH,
        0.95,
      );
    }
  }

  /**
   * Polyline arc: `frac` of ARC_SPAN_RAD starting at ARC_START_RAD,
   * drawn as short lineBetween chords (mock-safe — no Graphics.arc).
   */
  private strokeArc(
    g: Phaser.GameObjects.Graphics,
    radius: number,
    frac: number,
    color: number,
    width: number,
    alpha: number,
  ): void {
    const steps = Math.max(2, Math.round(ARC_SEGMENTS * frac));
    const span = ARC_SPAN_RAD * frac;
    g.lineStyle(width, color, alpha);
    let prevX = Math.cos(ARC_START_RAD) * radius;
    let prevY = Math.sin(ARC_START_RAD) * radius;
    for (let i = 1; i <= steps; i++) {
      const a = ARC_START_RAD + (span * i) / steps;
      const x = Math.cos(a) * radius;
      const y = Math.sin(a) * radius;
      g.lineBetween(prevX, prevY, x, y);
      prevX = x;
      prevY = y;
    }
  }

  /** PSV-1729 — feed the own-ship energy arc (null hides it). */
  updateEnergyArc(userId: string, energy: number, cap: number): void {
    const entry = this.hpBars.get(userId);
    if (entry === undefined) return;
    entry.energyFrac = cap > 0 ? energy / cap : null;
    this.redrawHpBar(entry);
  }

  // ---------- hit flash ----------

  spawnHitFlash(
    shipSprite: Phaser.GameObjects.Sprite,
    userId: string,
  ): void {
    const existing = this.hitFlashes.get(userId);
    if (existing !== undefined) {
      // Already flashing — reset countdown for multi-hit pulse effect.
      // Keep originalTint as-is; we want to restore to the *pre-flash*
      // tint at the end, not whatever tint we set during the flash.
      existing.remaining = HIT_FLASH_MS;
      return;
    }
    // `tintTopLeft` reads the current multiply tint (0xffffff = no tint).
    // Saving before setTintFill so we can return to the prior tint mode
    // on expiry — important for remote ships which carry COLOR_REMOTE.
    const originalTint = shipSprite.tintTopLeft;
    shipSprite.setTintFill(0xffffff);
    this.hitFlashes.set(userId, {
      sprite: shipSprite,
      originalTint,
      remaining: HIT_FLASH_MS,
    });
  }

  // ---------- particle effects ----------

  spawnHitSpark(x: number, y: number): void {
    const count = randInt(HIT_SPARK_COUNT_MIN, HIT_SPARK_COUNT_MAX);
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = randRange(HIT_SPARK_SPEED_MIN, HIT_SPARK_SPEED_MAX);
      this.spawnParticle({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        lifetime: randRange(HIT_SPARK_LIFETIME_MIN, HIT_SPARK_LIFETIME_MAX),
        color: pickColor(HIT_SPARK_COLORS),
        size: randInt(2, 3),
        alphaStart: 1,
      });
    }
  }

  spawnDeathExplosion(x: number, y: number, paletteColor?: number): void {
    const count = randInt(DEATH_PARTICLE_COUNT_MIN, DEATH_PARTICLE_COUNT_MAX);
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = randRange(
        DEATH_PARTICLE_SPEED_MIN,
        DEATH_PARTICLE_SPEED_MAX,
      );
      // PSV-1888 — an equipped death effect recolors ~2/3 of the debris
      // (a full recolor reads flat; mixing keeps the explosion lively).
      const color =
        paletteColor !== undefined && Math.random() < 0.67
          ? paletteColor
          : pickColor(DEATH_PARTICLE_COLORS);
      this.spawnParticle({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        lifetime: randRange(
          DEATH_PARTICLE_LIFETIME_MIN,
          DEATH_PARTICLE_LIFETIME_MAX,
        ),
        color,
        size: randInt(3, 5),
        alphaStart: 1,
      });
    }
    const ring = this.scene.add.graphics();
    ring.setDepth(4);
    this.deathRings.push({ graphic: ring, x, y, age: 0 });
  }

  spawnMuzzleFlash(x: number, y: number, angle: number): void {
    const noseX = x + Math.cos(angle) * MUZZLE_OFFSET_FORWARD;
    const noseY = y + Math.sin(angle) * MUZZLE_OFFSET_FORWARD;
    for (let i = 0; i < MUZZLE_COUNT; i++) {
      const spread = (Math.random() - 0.5) * 0.6; // ±0.3 rad cone
      const a = angle + spread;
      const speed = randRange(MUZZLE_SPEED_MIN, MUZZLE_SPEED_MAX);
      this.spawnParticle({
        x: noseX,
        y: noseY,
        vx: Math.cos(a) * speed,
        vy: Math.sin(a) * speed,
        lifetime: MUZZLE_LIFETIME,
        color: pickColor(MUZZLE_COLORS),
        size: randInt(2, 3),
        alphaStart: 1,
      });
    }
  }

  /**
   * PSV-1729 — engine exhaust: 1-2 short-lived particles pushed out the
   * stern while thrust is held. Caller invokes once per render frame for
   * the OWN ship only (remote thrust isn't known client-side).
   */
  addEngineFlame(x: number, y: number, angle: number, trailColor?: number): void {
    const backX = x - Math.cos(angle) * (SHIP_LENGTH / 2 + 4);
    const backY = y - Math.sin(angle) * (SHIP_LENGTH / 2 + 4);
    const jitter = (Math.random() - 0.5) * 0.6;
    // PSV-1888 — an equipped trail recolors the flame (white sparks mixed
    // in so the exhaust keeps depth).
    const color =
      trailColor !== undefined
        ? Math.random() < 0.75
          ? trailColor
          : 0xffffff
        : Math.random() < 0.5
          ? 0x35c4f0
          : 0xffaa44;
    this.spawnParticle({
      x: backX,
      y: backY,
      vx: -Math.cos(angle + jitter) * 90,
      vy: -Math.sin(angle + jitter) * 90,
      lifetime: 260,
      color,
      size: 2.5,
      alphaStart: 0.8,
    });
  }

  addProjectileTrail(projectileSprite: Phaser.GameObjects.Graphics): void {
    this.spawnParticle({
      x: projectileSprite.x,
      y: projectileSprite.y,
      vx: 0,
      vy: 0,
      lifetime: TRAIL_LIFETIME,
      color: TRAIL_COLOR,
      size: TRAIL_SIZE,
      alphaStart: TRAIL_ALPHA_START,
    });
  }

  private spawnParticle(spec: {
    x: number;
    y: number;
    vx: number;
    vy: number;
    lifetime: number;
    color: number;
    size: number;
    alphaStart: number;
  }): void {
    const p = this.acquireParticle();
    if (p === null) return; // pool exhausted — silently drop
    p.active = true;
    p.x = spec.x;
    p.y = spec.y;
    p.vx = spec.vx;
    p.vy = spec.vy;
    p.age = 0;
    p.lifetime = spec.lifetime;
    p.color = spec.color;
    p.size = spec.size;
    p.alphaStart = spec.alphaStart;
    const g = p.graphic;
    g.clear();
    g.fillStyle(spec.color, 1);
    g.fillCircle(0, 0, spec.size);
    g.setPosition(spec.x, spec.y);
    g.setAlpha(spec.alphaStart);
    g.setVisible(true);
    g.setDepth(3);
  }

  private acquireParticle(): Particle | null {
    for (const p of this.pool) {
      if (!p.active) return p;
    }
    return null;
  }

  private releaseParticle(p: Particle): void {
    p.active = false;
    p.graphic.clear();
    p.graphic.setVisible(false);
  }

  // ---------- you-died overlay ----------

  showYouDied(): void {
    if (this.youDiedText !== null) return;
    const cam = this.scene.cameras.main;
    this.youDiedText = this.scene.add
      .text(cam.centerX, cam.centerY - 20, 'YOU DIED', {
        fontSize: '48px',
        color: '#ff3333',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20);
    this.youDiedCountdownText = this.scene.add
      .text(cam.centerX, cam.centerY + 30, '3', {
        fontSize: '32px',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20);
    this.youDiedRemaining = YOU_DIED_DURATION_MS;
    this.youDiedText.setScale(1);
    this.scene.tweens.add({
      targets: this.youDiedText,
      scale: 1.2,
      duration: 200,
      ease: 'Quad.easeOut',
    });
  }

  hideYouDied(): void {
    if (this.youDiedText !== null) {
      this.youDiedText.destroy();
      this.youDiedText = null;
    }
    if (this.youDiedCountdownText !== null) {
      this.youDiedCountdownText.destroy();
      this.youDiedCountdownText = null;
    }
    this.youDiedRemaining = 0;
  }

  // ---------- kill feed ----------

  /**
   * PSV-1886 — free-form feed line (streak announcements etc.), same
   * slide-in/aging pipeline as kill entries.
   */
  addFeedLine(message: string, color: string): void {
    const text = this.scene.add
      .text(this.killFeedAnchorX + 100, this.killFeedAnchorY, message, {
        fontSize: '14px',
        color,
        fontStyle: 'bold',
      })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(15);
    this.killFeedEntries.unshift({ text, age: 0 });
    this.scene.tweens.add({
      targets: text,
      x: this.killFeedAnchorX,
      duration: 200,
    });
  }

  addKillFeedEntry(
    killerName: string,
    victimName: string,
    isOwnKill: boolean,
    isOwnDeath: boolean,
  ): void {
    const color = isOwnKill ? '#44ff44' : isOwnDeath ? '#ff3333' : '#ffffff';
    const text = this.scene.add
      .text(
        this.killFeedAnchorX + 100, // start off-screen for slide-in
        this.killFeedAnchorY,
        `${killerName} ⚔ ${victimName}`,
        {
          fontSize: '14px',
          color,
        },
      )
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(15);
    this.killFeedEntries.unshift({ text, age: 0 });

    // Slide-in tween from off-screen to anchor x
    this.scene.tweens.add({
      targets: text,
      x: this.killFeedAnchorX,
      duration: KILL_FEED_SLIDE_DURATION_MS,
      ease: 'Quad.easeOut',
    });

    // Restack: place each visible entry below the newest
    this.restackKillFeed();

    // Trim past max
    while (this.killFeedEntries.length > KILL_FEED_MAX) {
      const oldest = this.killFeedEntries.pop();
      if (oldest !== undefined) oldest.text.destroy();
    }
  }

  private restackKillFeed(): void {
    for (let i = 0; i < this.killFeedEntries.length; i++) {
      const entry = this.killFeedEntries[i]!;
      entry.text.y = this.killFeedAnchorY + i * KILL_FEED_LINE_HEIGHT;
    }
  }

  // ---------- per-frame update ----------

  update(deltaMs: number): void {
    if (this.destroyed) return;
    const dt = deltaMs / 1000;

    // 1. Particle motion + lifetime
    for (const p of this.pool) {
      if (!p.active) continue;
      p.age += deltaMs;
      if (p.age >= p.lifetime) {
        this.releaseParticle(p);
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      const lifeRemaining = 1 - p.age / p.lifetime;
      p.graphic.setPosition(p.x, p.y);
      p.graphic.setAlpha(p.alphaStart * lifeRemaining);
    }

    // 2. HP bar positions follow their owning ship sprites — both
    //    Graphics objects (outer + fill) move together.
    for (const entry of this.hpBars.values()) {
      const x = entry.sprite.x;
      // PSV-1729 — arcs wrap the hull: same origin as the sprite.
      const y = entry.sprite.y;
      entry.outer.setPosition(x, y);
      entry.fill.setPosition(x, y);
    }

    // 3. Hit flash — sprite is in setTintFill(white) mode for the
    //    duration. On expiry restore the pre-flash multiply tint
    //    (setTint also exits fill mode). No per-frame work needed
    //    until expiry since the tint moves with the sprite naturally.
    for (const [userId, flash] of this.hitFlashes) {
      flash.remaining -= deltaMs;
      if (flash.remaining <= 0) {
        flash.sprite.setTint(flash.originalTint);
        this.hitFlashes.delete(userId);
      }
    }

    // 4. Death rings — expanding alpha-fade
    for (let i = this.deathRings.length - 1; i >= 0; i--) {
      const ring = this.deathRings[i]!;
      ring.age += deltaMs;
      const t = ring.age / DEATH_RING_LIFETIME_MS;
      if (t >= 1) {
        ring.graphic.destroy();
        this.deathRings.splice(i, 1);
        continue;
      }
      const radius =
        DEATH_RING_RADIUS_START +
        (DEATH_RING_RADIUS_END - DEATH_RING_RADIUS_START) * t;
      ring.graphic.clear();
      ring.graphic.lineStyle(2, 0xffaa44, 1 - t);
      ring.graphic.strokeCircle(ring.x, ring.y, radius);
    }

    // 5. You-died countdown
    if (
      this.youDiedText !== null &&
      this.youDiedCountdownText !== null &&
      this.youDiedRemaining > 0
    ) {
      this.youDiedRemaining = Math.max(0, this.youDiedRemaining - deltaMs);
      const seconds = Math.ceil(this.youDiedRemaining / 1000);
      this.youDiedCountdownText.setText(String(seconds));
    }

    // 6. Kill feed — age + fade tail entries
    for (let i = this.killFeedEntries.length - 1; i >= 0; i--) {
      const entry = this.killFeedEntries[i]!;
      entry.age += deltaMs;
      if (entry.age >= KILL_FEED_LIFETIME_MS) {
        entry.text.destroy();
        this.killFeedEntries.splice(i, 1);
        this.restackKillFeed();
        continue;
      }
      const fadeStart = KILL_FEED_LIFETIME_MS - KILL_FEED_FADE_DURATION_MS;
      if (entry.age > fadeStart) {
        const fadeProgress =
          (entry.age - fadeStart) / KILL_FEED_FADE_DURATION_MS;
        entry.text.setAlpha(1 - fadeProgress);
      }
    }
  }

  // ---------- lifecycle ----------

  destroy(): void {
    this.destroyed = true;
    for (const p of this.pool) p.graphic.destroy();
    this.pool.length = 0;
    for (const entry of this.hpBars.values()) {
      if (entry.pulseTween !== null) entry.pulseTween.remove();
      entry.outer.destroy();
      entry.fill.destroy();
    }
    this.hpBars.clear();
    for (const flash of this.hitFlashes.values()) {
      flash.sprite.setTint(flash.originalTint);
    }
    this.hitFlashes.clear();
    for (const ring of this.deathRings) ring.graphic.destroy();
    this.deathRings.length = 0;
    for (const entry of this.killFeedEntries) entry.text.destroy();
    this.killFeedEntries.length = 0;
    this.hideYouDied();
  }
}

// ---------- helpers ----------

function randRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function randInt(min: number, max: number): number {
  return Math.floor(randRange(min, max + 1));
}

function pickColor(colors: readonly number[]): number {
  return colors[Math.floor(Math.random() * colors.length)]!;
}
